import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { onNewLead } from "@/lib/automations";
import type { Lang } from "@/lib/emailTemplates";

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ContactModal({ open, onClose }: ContactModalProps) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", company: "", brief: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setTimeout(() => {
        setSent(false);
        setError(null);
        setForm({ name: "", email: "", company: "", brief: "" });
      }, 350);
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    setError(null);

    const parts = form.name.trim().split(/\s+/);
    const first_name = parts[0];
    const last_name = parts.slice(1).join(" ") || "";
    const language = (i18n.language === "he" ? "he" : "en") as Lang;
    const emailClean = form.email.trim();

    const { data: newLead, error: insertError } = await supabase
      .from("leads")
      .insert({
        first_name,
        last_name,
        email: emailClean,
        emails: [emailClean],
        business_name: form.company.trim() || null,
        other_notes: form.brief.trim() || null,
        source: "contact_modal",
        status: "new",
        language,
      })
      .select("id")
      .single();

    if (insertError) {
      console.error("[ContactModal] Lead insert failed:", insertError);
      setLoading(false);
      setError(t("dyna.modal.err") as string);
      return;
    }

    onNewLead({
      email: emailClean,
      first_name,
      last_name,
      language,
      leadId: newLead?.id,
    }).catch(err => console.error("[ContactModal] onNewLead error:", err));

    setLoading(false);
    setSent(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="dyna-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            className="dyna-modal"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <button className="dyna-modal__close" onClick={onClose} aria-label={t("dyna.modal.close") as string}>✕</button>

            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  style={{ textAlign: "center", padding: "40px 0" }}
                >
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: "rgba(29,158,117,0.15)", border: "2px solid rgba(29,158,117,0.4)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 24px",
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="#1D9E75" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        strokeDasharray="24" strokeDashoffset="0"
                        style={{ animation: "dyna-checkmark 0.4s ease-out 0.1s both" }} />
                    </svg>
                  </div>
                  <h2 style={{ fontSize: 32, marginBottom: 12 }}>{t("dyna.modal.ok_title")}</h2>
                  <p>{t("dyna.modal.ok_body")}</p>
                  <button className="dyna-btn dyna-btn--primary" onClick={onClose} style={{ marginTop: 24, margin: "24px auto 0", display: "inline-flex" }}>
                    {t("dyna.modal.close")}
                  </button>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                  <h2>{t("dyna.modal.title")}</h2>
                  <p>{t("dyna.modal.sub")}</p>
                  <form className="dyna-modal__form" onSubmit={submit}>
                    <div className="dyna-modal__field">
                      <label className="dyna-modal__label">{t("dyna.modal.name_label")}</label>
                      <input className="dyna-modal__input" required placeholder={t("dyna.modal.name_ph")} value={form.name} onChange={update("name")} />
                    </div>
                    <div className="dyna-modal__field">
                      <label className="dyna-modal__label">{t("dyna.modal.email_label")}</label>
                      <input className="dyna-modal__input" type="email" dir="ltr" required placeholder={t("dyna.modal.email_ph")} value={form.email} onChange={update("email")} />
                    </div>
                    <div className="dyna-modal__field">
                      <label className="dyna-modal__label">{t("dyna.modal.co_label")}</label>
                      <input className="dyna-modal__input" placeholder={t("dyna.modal.co_ph")} value={form.company} onChange={update("company")} />
                    </div>
                    <div className="dyna-modal__field">
                      <label className="dyna-modal__label">{t("dyna.modal.brief_label")}</label>
                      <textarea className="dyna-modal__textarea" placeholder={t("dyna.modal.brief_ph")} value={form.brief} onChange={update("brief")} />
                    </div>
                    {error && (
                      <div style={{
                        fontSize: 13,
                        color: "rgba(220,80,80,1)",
                        padding: "10px 14px",
                        borderRadius: 8,
                        background: "rgba(220,80,80,0.08)",
                        border: "1px solid rgba(220,80,80,0.25)",
                        fontFamily: "Geist, system-ui, sans-serif",
                      }}>
                        {error}
                      </div>
                    )}
                    <button
                      className="dyna-btn dyna-btn--primary"
                      type="submit"
                      disabled={loading}
                      style={{ width: "100%", justifyContent: "center", borderRadius: 12, padding: "16px 24px", fontSize: 15 }}
                    >
                      {loading ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 0.7s linear infinite" }}>
                            <circle cx="8" cy="8" r="6" stroke="rgba(253,248,240,0.3)" strokeWidth="2" />
                            <path d="M8 2a6 6 0 0 1 6 6" stroke="var(--quartz-200)" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          {t("dyna.modal.sending")}
                        </span>
                      ) : (
                        <span>{t("dyna.modal.submit")} <span className="dyna-arrow">→</span></span>
                      )}
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
